/**
 * PTEL Andalucía - AbbreviationExpander
 * 
 * Servicio de expansión de abreviaturas de direcciones andaluzas.
 * Maximiza probabilidad de match en búsquedas DERA/WFS.
 * 
 * Fase 2 del pipeline de normalización textual:
 * 1. UTF-8/Mojibake (EncodingCorrector)
 * 2. Desconcatenación (textDeconcatenator)
 * 3. Expansión abreviaturas (abbreviationExpander) ← ESTE
 * 4. Normalización mayúsculas/tildes (charNormalizer)
 * 
 * @version 1.0.0
 * @date Enero 2026
 */

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export interface AbbreviationExpansionResult {
  /** Texto original */
  original: string;
  /** Texto con abreviaturas expandidas */
  expanded: string;
  /** Lista de expansiones aplicadas ["C/ → Calle", "Nº → Número"] */
  expansionsApplied: string[];
  /** Si se modificó el texto */
  wasModified: boolean;
}

interface AbbreviationEntry {
  /** Abreviatura a buscar */
  abbrev: string;
  /** Expansión completa */
  expansion: string;
  /** Requiere límite de palabra (espacio o inicio) */
  wordBoundary: boolean;
}

// ============================================================================
// DICCIONARIO DE ABREVIATURAS
// ============================================================================

/**
 * Abreviaturas ordenadas por longitud descendente.
 * Esto evita conflictos (ej: "Avda." antes que "Av.").
 * wordBoundary = true significa que solo expande si está al inicio o después de espacio.
 */
const ABBREVIATIONS: AbbreviationEntry[] = [
  // Vías principales (con variantes)
  { abbrev: 'Avda.', expansion: 'Avenida', wordBoundary: true },
  { abbrev: 'Avda', expansion: 'Avenida', wordBoundary: true },
  { abbrev: 'Avd.', expansion: 'Avenida', wordBoundary: true },
  { abbrev: 'Ctra.', expansion: 'Carretera', wordBoundary: true },
  { abbrev: 'Ctra', expansion: 'Carretera', wordBoundary: true },
  { abbrev: 'Rbla.', expansion: 'Rambla', wordBoundary: true },
  { abbrev: 'Trav.', expansion: 'Travesía', wordBoundary: true },
  { abbrev: 'Tvª.', expansion: 'Travesía', wordBoundary: true },
  { abbrev: 'Glta.', expansion: 'Glorieta', wordBoundary: true },
  { abbrev: 'Psje.', expansion: 'Pasaje', wordBoundary: true },
  { abbrev: 'Pque.', expansion: 'Parque', wordBoundary: true },
  { abbrev: 'Polg.', expansion: 'Polígono', wordBoundary: true },
  { abbrev: 'Cmno.', expansion: 'Camino', wordBoundary: true },
  { abbrev: 'Edif.', expansion: 'Edificio', wordBoundary: true },
  { abbrev: 'Dtor.', expansion: 'Doctor', wordBoundary: true },
  { abbrev: 'Gral.', expansion: 'General', wordBoundary: true },
  { abbrev: 'Mpal.', expansion: 'Municipal', wordBoundary: true },
  { abbrev: 'Prov.', expansion: 'Provincial', wordBoundary: true },
  { abbrev: 'Cial.', expansion: 'Comercial', wordBoundary: true },
  { abbrev: 'Ctjo.', expansion: 'Cortijo', wordBoundary: true },
  { abbrev: 'Ptda.', expansion: 'Partida', wordBoundary: true },
  { abbrev: 'Tte.', expansion: 'Teniente', wordBoundary: true },
  { abbrev: 'Cte.', expansion: 'Comandante', wordBoundary: true },
  { abbrev: 'Nac.', expansion: 'Nacional', wordBoundary: true },
  { abbrev: 'Ind.', expansion: 'Industrial', wordBoundary: true },
  { abbrev: 'Pza.', expansion: 'Plaza', wordBoundary: true },
  { abbrev: 'Pje.', expansion: 'Pasaje', wordBoundary: true },
  { abbrev: 'Pol.', expansion: 'Polígono', wordBoundary: true },
  { abbrev: 'Urb.', expansion: 'Urbanización', wordBoundary: true },
  { abbrev: 'Bda.', expansion: 'Barriada', wordBoundary: true },
  { abbrev: 'Bª.', expansion: 'Barriada', wordBoundary: true },
  { abbrev: 'Cno.', expansion: 'Camino', wordBoundary: true },
  { abbrev: 'Sto.', expansion: 'Santo', wordBoundary: true },
  { abbrev: 'Sta.', expansion: 'Santa', wordBoundary: true },
  { abbrev: 'Sra.', expansion: 'Señora', wordBoundary: true },
  { abbrev: 'Dra.', expansion: 'Doctora', wordBoundary: true },
  { abbrev: 'Av.', expansion: 'Avenida', wordBoundary: true },
  { abbrev: 'Pl.', expansion: 'Plaza', wordBoundary: true },
  { abbrev: 'Cl.', expansion: 'Calle', wordBoundary: true },
  { abbrev: 'Ps.', expansion: 'Paseo', wordBoundary: true },
  { abbrev: 'Ed.', expansion: 'Edificio', wordBoundary: true },
  { abbrev: 'Dr.', expansion: 'Doctor', wordBoundary: true },
  { abbrev: 'Sr.', expansion: 'Señor', wordBoundary: true },
  { abbrev: 'Cl/', expansion: 'Calle', wordBoundary: true },
  { abbrev: 'C/', expansion: 'Calle', wordBoundary: true },
  { abbrev: 'c/', expansion: 'Calle', wordBoundary: true },
  { abbrev: 'Pº', expansion: 'Paseo', wordBoundary: true },
  { abbrev: 'P.º', expansion: 'Paseo', wordBoundary: true },
  { abbrev: 'S.', expansion: 'San', wordBoundary: true },
  
  // Numeración (sin wordBoundary para capturar en cualquier posición)
  { abbrev: 'N.º', expansion: 'Número', wordBoundary: false },
  { abbrev: 'S/N', expansion: 'Sin Número', wordBoundary: false },
  { abbrev: 's/n', expansion: 'Sin Número', wordBoundary: false },
  { abbrev: 'Nº', expansion: 'Número', wordBoundary: false },
  { abbrev: 'nº', expansion: 'Número', wordBoundary: false },
  { abbrev: 'n°', expansion: 'Número', wordBoundary: false },
].sort((a, b) => b.abbrev.length - a.abbrev.length);

// Crear un Set de expansiones para evitar doble expansión
const _EXPANDED_FORMS = new Set(ABBREVIATIONS.map(a => a.expansion.toLowerCase()));

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Expande abreviaturas en el texto con información detallada.
 * 
 * @param text - Texto a procesar
 * @returns Resultado con texto expandido y metadatos
 */
export function expandAbbreviations(text: string): AbbreviationExpansionResult {
  // Early-exit para texto vacío
  if (!text || text.trim().length === 0) {
    return {
      original: text,
      expanded: text,
      expansionsApplied: [],
      wasModified: false,
    };
  }

  let result = text;
  const expansionsApplied: string[] = [];

  for (const entry of ABBREVIATIONS) {
    // Verificar si ya está expandido (evitar "Calle" → "CalleCalle")
    if (isAlreadyExpanded(result, entry.expansion)) {
      continue;
    }

    // Buscar y reemplazar
    const newResult = applyExpansion(result, entry);
    
    if (newResult !== result) {
      expansionsApplied.push(`${entry.abbrev} → ${entry.expansion}`);
      result = newResult;
    }
  }

  return {
    original: text,
    expanded: result,
    expansionsApplied,
    wasModified: result !== text,
  };
}

/**
 * Versión simplificada que solo retorna el texto expandido.
 * Para uso en pipeline.
 * 
 * @param text - Texto a procesar
 * @returns Texto con abreviaturas expandidas
 */
export function expandAbbreviationsSimple(text: string): string {
  return expandAbbreviations(text).expanded;
}

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Verifica si el texto ya contiene la forma expandida.
 * Evita dobles expansiones como "Calle Real" → "CalleCalle Real".
 */
function isAlreadyExpanded(text: string, expansion: string): boolean {
  return text.toLowerCase().includes(expansion.toLowerCase());
}

/**
 * Aplica una expansión respetando límites de palabra si es necesario.
 */
function applyExpansion(text: string, entry: AbbreviationEntry): string {
  const { abbrev, expansion, wordBoundary } = entry;
  
  if (wordBoundary) {
    // Solo expandir si está al inicio o después de espacio/puntuación
    // Regex: inicio de string o espacio/puntuación antes de la abreviatura
    const escapedAbbrev = escapeRegExp(abbrev);
    const regex = new RegExp(`(^|[\\s,;:(])${escapedAbbrev}`, 'g');
    
    return text.replace(regex, (match, prefix) => {
      return prefix + expansion;
    });
  } else {
    // Reemplazo simple sin restricción de límite
    return text.split(abbrev).join(expansion);
  }
}

/**
 * Escapa caracteres especiales para uso en RegExp.
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&');
}
